import React from 'react';
import { Lightbulb } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import AlarmDot from '../../components/shared/AlarmDot';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import DonutChart from '../../components/charts/DonutChart';
import { nationalAlarms, faultDistribution, nationalKPIs } from '../../data/mockData';
import { AlarmSeverity } from '../../types';
import { relativeTime } from '../../utils/dateHelpers';

export default function NationalAlarms() {
  const activeAlarms = nationalAlarms.filter(a => a.status === 'ACTIVE');

  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <KPICard title="Active Alarms" value={nationalKPIs.activeAlarms.toLocaleString()} bgColor="bg-red-50" subtextColor="red" subtext="Across all states" />
        <KPICard title="Critical" value={nationalKPIs.criticalAlarms} bgColor="bg-red-50" subtextColor="red" subtext="Needs immediate action" />
        <KPICard title="Warning" value={nationalKPIs.warningAlarms} bgColor="bg-orange-50" subtextColor="amber" subtext="Monitor closely" />
        <KPICard title="Info / Comms" value={nationalKPIs.infoAlarms} subtext="Communication faults" />
        <KPICard title="Resolved Today" value={nationalKPIs.resolvedToday} subtextColor="green" subtext={`MTTR: ${nationalKPIs.mttrHours}h avg`} />
      </div>

      {/* Two-column */}
      <div className="flex gap-4">
        {/* Live alarm feed */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Live Alarm Feed" subtitle="Most recent active alarms" />
          <div className="space-y-3">
            {nationalAlarms.map(alarm => (
              <div key={alarm.id} className={`flex gap-3 p-3 rounded-input border ${alarm.status === 'RESOLVED' ? 'border-border/40 opacity-70' : 'border-border'}`}>
                <AlarmDot severity={alarm.severity} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-text-primary leading-tight">{alarm.description}</p>
                    <StatusPill
                      status={alarm.severity === AlarmSeverity.CRITICAL ? 'critical' : alarm.severity === AlarmSeverity.WARNING ? 'warning' : 'info'}
                      label={alarm.severity === AlarmSeverity.CRITICAL ? 'Critical' : alarm.severity === AlarmSeverity.WARNING ? 'Warning' : 'Info'}
                    />
                  </div>
                  <p className="text-xs text-text-tertiary mt-1">
                    <span className="font-mono">{alarm.plantId}</span> · {alarm.state} · {relativeTime(alarm.timestamp)} · {alarm.duration}
                  </p>
                  {alarm.status === 'ACTIVE' && (
                    <button className="mt-2 flex items-center gap-1 text-xs text-accent hover:underline">
                      <Lightbulb size={11} /> Get advice
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button className="mt-3 text-xs text-accent hover:underline w-full text-center">View full alarm log →</button>
        </div>

        {/* Fault distribution */}
        <div className="w-64 flex-shrink-0 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Fault Distribution" subtitle="Active alarms by type" />
          <DonutChart data={faultDistribution} height={200} />
        </div>
      </div>
    </div>
  );
}
